/**
 * MessageActions — the long-press message actions shared by every chat
 * surface: the conversation bubbles (MessageBubble: drawer, DMs, support) and
 * the chat rows in the game log (ChatLogRow).
 *
 *   useMessageActions  tray/picker/copy state + the press/long-press rules
 *   MessageActionTray  quick reactions, more-emoji, copy, flag
 *   ReactionChips      grouped reaction counts (tap to toggle; web hover names)
 *   EmojiPickerModal   the full reaction grid
 *
 * One implementation so the bubble and the log row can't drift.
 */
import React, { useEffect, useRef, useState } from 'react'
import {
  Keyboard,
  Modal,
  Pressable,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
  type StyleProp,
  type ViewStyle,
} from 'react-native'
import * as Clipboard from 'expo-clipboard'
import { CHAT_REACTION_EMOJIS, type ChatReactionEmoji } from '@warcarp/api-types'
import { reportError } from '../../lib/reporting'
import { longPressHaptic } from '../../lib/haptics'
import { MoreReactionsIcon } from '../ui/MoreReactionsIcon'

export interface MessageReaction {
  emoji: ChatReactionEmoji
  userId: string
  displayName: string
}

const QUICK_REACTIONS = CHAT_REACTION_EMOJIS.slice(0, 2)
// A normal View includes its 1px borders inside width on web. The animated
// version this replaces exposed 272px of inner layout space, so retain that
// exact geometry with 2px for the border: 7 × 32px emoji cells, 6 × 6px gaps,
// and 6px padding on both sides.
const EMOJI_PICKER_WIDTH = 274
const EMOJI_PICKER_HEIGHT = 238
const EMOJI_PICKER_MARGIN = 8
const REACTION_NAMES: Partial<Record<ChatReactionEmoji, string>> = {
  '👍': 'thumbs up',
  '👎': 'thumbs down',
  '❤️': 'heart',
  '😂': 'laughing',
  '😮': 'surprised',
  '😢': 'sad',
  '🎉': 'party popper',
  '🔥': 'fire',
}

function reactionName(emoji: ChatReactionEmoji): string {
  return REACTION_NAMES[emoji] ?? emoji
}

/**
 * Screen-reader view of a message's reactions, for rows that are a single
 * accessibility element (iOS hides the chips inside them): a spoken summary
 * to append to the row label, and one toggle action per reaction.
 */
export function reactionsA11y(reactions: MessageReaction[], currentUserId: string | undefined): {
  summary: string
  actions: { name: string; label: string; emoji: ChatReactionEmoji }[]
} {
  const groups = CHAT_REACTION_EMOJIS
    .map((emoji) => ({ emoji, grouped: reactions.filter((reaction) => reaction.emoji === emoji) }))
    .filter(({ grouped }) => grouped.length > 0)
  return {
    summary: groups
      .map(({ emoji, grouped }) => `${grouped.length} ${reactionName(emoji)} reaction${grouped.length === 1 ? '' : 's'}`)
      .join(', '),
    actions: groups.map(({ emoji, grouped }) => ({
      name: `react:${emoji}`,
      label: `${grouped.some((reaction) => reaction.userId === currentUserId) ? 'Remove' : 'Add'} ${reactionName(emoji)} reaction`,
      emoji,
    })),
  }
}

function formatReactorNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? 'Someone'
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`
}

/** Tray, picker and copy state for one message, plus its press rules. */
export function useMessageActions({
  messageId,
  content,
  alignEnd,
  onReact,
}: {
  messageId: string
  content: string
  /** Right-align the picker to the message (own messages). */
  alignEnd: boolean
  onReact?: (messageId: string, emoji: ChatReactionEmoji) => void
}) {
  const [trayOpen, setTrayOpen] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerPosition, setPickerPosition] = useState({ left: 0, top: 0 })
  const [copied, setCopied] = useState(false)
  const longPressedRef = useRef(false)
  const copyResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const trayRef = useRef<View | null>(null)
  const { width: viewportWidth, height: viewportHeight } = useWindowDimensions()

  useEffect(() => () => {
    if (copyResetTimerRef.current) clearTimeout(copyResetTimerRef.current)
  }, [])

  const close = () => {
    longPressedRef.current = false
    setTrayOpen(false)
    setPickerOpen(false)
  }

  const openPicker = () => {
    setTrayOpen(true)
    const place = (x: number, y: number, width: number, height: number) => {
      const alignedLeft = alignEnd ? x + width - EMOJI_PICKER_WIDTH : x
      const left = Math.max(
        EMOJI_PICKER_MARGIN,
        Math.min(alignedLeft, viewportWidth - EMOJI_PICKER_WIDTH - EMOJI_PICKER_MARGIN),
      )
      const above = y - EMOJI_PICKER_HEIGHT - 4
      const below = y + height + 4
      const top = above >= EMOJI_PICKER_MARGIN
        ? above
        : Math.max(
            EMOJI_PICKER_MARGIN,
            Math.min(below, viewportHeight - EMOJI_PICKER_HEIGHT - EMOJI_PICKER_MARGIN),
          )
      setPickerPosition({ left, top })
      setPickerOpen(true)
    }
    if (trayRef.current?.measureInWindow) {
      trayRef.current.measureInWindow(place)
      return
    }
    place((viewportWidth - EMOJI_PICKER_WIDTH) / 2, (viewportHeight - EMOJI_PICKER_HEIGHT) / 2, EMOJI_PICKER_WIDTH, 0)
  }

  const toggleReaction = (emoji: ChatReactionEmoji) => {
    onReact?.(messageId, emoji)
    close()
  }

  const copy = async () => {
    try {
      await Clipboard.setStringAsync(content)
      setCopied(true)
      if (copyResetTimerRef.current) clearTimeout(copyResetTimerRef.current)
      copyResetTimerRef.current = setTimeout(() => setCopied(false), 1200)
    } catch (err) {
      reportError(err, { tags: { area: 'thread-copy' }, extra: { messageId } })
    }
  }

  /** A tap only closes an open tray (and swallows the tap that ends a long press). */
  const handlePress = () => {
    if (longPressedRef.current) {
      longPressedRef.current = false
      return
    }
    if (trayOpen) close()
  }

  const handleLongPress = () => {
    Keyboard.dismiss()
    longPressHaptic()
    longPressedRef.current = true
    setTrayOpen(true)
    setPickerOpen(false)
  }

  const handlePressOut = () => {
    if (longPressedRef.current) {
      setTimeout(() => {
        longPressedRef.current = false
      }, 0)
    }
  }

  return {
    trayOpen,
    pickerOpen,
    pickerPosition,
    copied,
    trayRef,
    close,
    openPicker,
    toggleReaction,
    copy,
    handlePress,
    handleLongPress,
    handlePressOut,
    setPickerOpen,
  }
}

export function MessageActionTray({
  trayRef,
  reactions,
  currentUserId,
  isReacting,
  canReact,
  copied,
  onToggleReaction,
  onOpenPicker,
  onCopy,
  onReport,
}: {
  trayRef: React.RefObject<View | null>
  reactions: MessageReaction[]
  currentUserId?: string
  isReacting?: boolean
  canReact: boolean
  copied: boolean
  onToggleReaction: (emoji: ChatReactionEmoji) => void
  onOpenPicker: () => void
  onCopy: () => void
  /** Present only for other players' messages. */
  onReport?: () => void
}) {
  return (
    <View
      ref={trayRef}
      className="flex-row items-center bg-canvas border border-olive"
      accessibilityLabel="Message actions"
      style={{
        gap: 2,
        padding: 3,
        borderRadius: 14,
        shadowColor: '#000',
        shadowOpacity: 0.18,
        shadowRadius: 4,
        shadowOffset: { width: 0, height: 2 },
        elevation: 5,
      }}
    >
      {canReact &&
        QUICK_REACTIONS.map((emoji) => {
          const selected = reactions.some(
            (reaction) => reaction.emoji === emoji && reaction.userId === currentUserId,
          )
          return (
            <TouchableOpacity
              key={emoji}
              onPress={() => onToggleReaction(emoji)}
              disabled={isReacting}
              accessibilityRole="button"
              accessibilityLabel={`${selected ? 'Remove' : 'React with'} ${reactionName(emoji)} reaction`}
              accessibilityState={{ selected, disabled: !!isReacting }}
              className={selected ? 'bg-olive border border-olive-up' : 'bg-paper'}
              style={{
                width: 32,
                height: 30,
                borderRadius: 10,
                alignItems: 'center',
                justifyContent: 'center',
                opacity: isReacting ? 0.5 : 1,
              }}
            >
              <Text style={{ fontSize: 17 }}>{emoji}</Text>
            </TouchableOpacity>
          )
        })}
      {canReact && (
        <TouchableOpacity
          onPress={onOpenPicker}
          accessibilityRole="button"
          accessibilityLabel="Open emoji picker"
          className="bg-paper"
          style={{ width: 32, height: 30, borderRadius: 10, alignItems: 'center', justifyContent: 'center' }}
        >
          <MoreReactionsIcon />
        </TouchableOpacity>
      )}
      <TouchableOpacity
        onPress={onCopy}
        accessibilityRole="button"
        accessibilityLabel={copied ? 'Message copied' : 'Copy message'}
        className="bg-paper"
        style={{ width: 32, height: 30, borderRadius: 10, alignItems: 'center', justifyContent: 'center' }}
      >
        <Text className="text-ink" style={{ fontSize: 15 }}>
          {copied ? '✓' : '⧉'}
        </Text>
      </TouchableOpacity>
      {onReport && (
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel="Flag as inappropriate"
          onPress={onReport}
          className="bg-paper"
          style={{ width: 32, height: 30, borderRadius: 10, alignItems: 'center', justifyContent: 'center' }}
        >
          <Text className="text-blood" style={{ fontSize: 17 }}>⚑</Text>
        </TouchableOpacity>
      )}
    </View>
  )
}

export function ReactionChips({
  reactions,
  currentUserId,
  isReacting,
  canReact,
  alignEnd,
  onToggleReaction,
  onHoverChange,
  size = 'regular',
  maxPerRow = 3,
  style,
}: {
  reactions: MessageReaction[]
  currentUserId?: string
  isReacting?: boolean
  canReact: boolean
  /** Right-align chips and their hover tooltip. */
  alignEnd: boolean
  onToggleReaction: (emoji: ChatReactionEmoji) => void
  /** Lets the host lift its stacking order while a tooltip is showing. */
  onHoverChange?: (hovering: boolean) => void
  /** `small` = compact chips for log rows (minimal overlap of the text). */
  size?: 'regular' | 'small'
  maxPerRow?: number
  style?: StyleProp<ViewStyle>
}) {
  const small = size === 'small'
  const [hovered, setHovered] = useState<ChatReactionEmoji | null>(null)
  const groups = CHAT_REACTION_EMOJIS.map((emoji) => ({
    emoji,
    reactions: reactions.filter((reaction) => reaction.emoji === emoji),
  })).filter((group) => group.reactions.length > 0)
  useEffect(() => { onHoverChange?.(hovered !== null) }, [hovered, onHoverChange])
  if (groups.length === 0) return null
  const columns = Math.max(1, Math.min(3, maxPerRow))

  return (
    <View
      className="items-center"
      style={[{ gap: small ? 3 : 4, flexDirection: 'column', alignItems: alignEnd ? 'flex-end' : 'flex-start' }, style]}
    >
      {Array.from({ length: Math.ceil(groups.length / columns) }, (_, row) => <View key={row} testID="reaction-chip-row" className="flex-row items-center" style={{ gap: small ? 3 : 4 }}>
      {groups.slice(row * columns, row * columns + columns).map(({ emoji, reactions: grouped }) => {
        const selected = grouped.some((reaction) => reaction.userId === currentUserId)
        const names = grouped.map((reaction) => reaction.displayName)
        const namesLabel = names.join(', ')
        return (
          <View
            key={emoji}
            onPointerEnter={() => setHovered(emoji)}
            onPointerLeave={() => setHovered((current) => current === emoji ? null : current)}
            style={{ position: 'relative' }}
          >
            {hovered === emoji && (
              <View
                pointerEvents="none"
                accessibilityLabel={`${reactionName(emoji)} reactions from ${namesLabel}`}
                className="bg-paper-up border border-olive"
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  ...(alignEnd ? { right: 0 } : { left: 0 }),
                  zIndex: 40,
                  width: 160,
                  marginBottom: 8,
                  padding: 8,
                  borderRadius: 10,
                  alignItems: 'center',
                  shadowColor: '#000',
                  shadowOpacity: 0.28,
                  shadowRadius: 8,
                  shadowOffset: { width: 0, height: 4 },
                  elevation: 10,
                }}
              >
                <View
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 7,
                    backgroundColor: '#fff',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 6,
                  }}
                >
                  <Text style={{ fontSize: 30, lineHeight: 36 }}>{emoji}</Text>
                </View>
                <Text className="text-ink" style={{ fontSize: 12, lineHeight: 16, textAlign: 'center', fontWeight: '600' }}>
                  {formatReactorNames(names)} reacted with {emoji}
                </Text>
              </View>
            )}
            <TouchableOpacity
              onPress={() => onToggleReaction(emoji)}
              disabled={isReacting || !canReact}
              accessibilityRole="button"
              accessibilityLabel={`${selected ? 'Remove' : 'Add'} ${reactionName(emoji)} reaction. ${grouped.length} reaction${grouped.length === 1 ? '' : 's'} from ${namesLabel}`}
              accessibilityState={{ selected, disabled: !!isReacting || !canReact }}
              className={selected ? 'bg-olive border-2 border-olive-up' : 'bg-paper border border-olive'}
              style={{
                minWidth: small ? 24 : 31,
                height: small ? 20 : 24,
                paddingHorizontal: small ? 4 : 6,
                borderRadius: small ? 10 : 12,
                alignItems: 'center',
                justifyContent: 'center',
                opacity: isReacting ? 0.5 : 1,
              }}
            >
              <View className="flex-row items-center" style={{ gap: 2 }}>
                <Text style={{ fontSize: small ? 12 : 13, lineHeight: small ? 16 : 17 }}>{emoji}</Text>
                <Text className={selected ? 'text-paper dark:text-canvas' : 'text-ink'} style={{ fontSize: small ? 10 : 13 }}>{grouped.length}</Text>
              </View>
            </TouchableOpacity>
          </View>
        )
      })}</View>)}
    </View>
  )
}

export function EmojiPickerModal({
  position,
  reactions,
  currentUserId,
  isReacting,
  onToggleReaction,
  onClose,
}: {
  position: { left: number; top: number }
  reactions: MessageReaction[]
  currentUserId?: string
  isReacting?: boolean
  onToggleReaction: (emoji: ChatReactionEmoji) => void
  onClose: () => void
}) {
  return (
    <Modal visible transparent animationType="none" onRequestClose={onClose}>
      <View style={{ flex: 1 }}>
        <Pressable
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Dismiss emoji picker"
          style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
        />
        <View
          className="bg-canvas border border-olive"
          accessibilityLabel="Emoji picker"
          style={{
            position: 'absolute',
            left: position.left,
            top: position.top,
            width: EMOJI_PICKER_WIDTH,
            borderRadius: 16,
            overflow: 'hidden',
            shadowColor: '#000',
            shadowOpacity: 0.2,
            shadowRadius: 6,
            shadowOffset: { width: 0, height: 3 },
            elevation: 20,
          }}
        >
          <View className="flex-row items-center border-b border-olive" style={{ height: 40, paddingHorizontal: 12 }}>
            <Text className="flex-1 text-ink font-stencil text-xs uppercase">Choose a reaction</Text>
            <TouchableOpacity
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Close emoji picker"
              style={{ width: 30, height: 30, alignItems: 'center', justifyContent: 'center' }}
            >
              <Text className="text-ink" style={{ fontSize: 20 }}>×</Text>
            </TouchableOpacity>
          </View>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, padding: 6 }}>
            {CHAT_REACTION_EMOJIS.map((emoji) => {
              const selected = reactions.some(
                (reaction) => reaction.emoji === emoji && reaction.userId === currentUserId,
              )
              return (
                <TouchableOpacity
                  key={emoji}
                  onPress={() => onToggleReaction(emoji)}
                  disabled={isReacting}
                  accessibilityRole="button"
                  accessibilityLabel={`${selected ? 'Remove' : 'React with'} ${reactionName(emoji)} reaction`}
                  accessibilityState={{ selected, disabled: !!isReacting }}
                  className={selected ? 'bg-olive border border-olive-up' : 'bg-paper'}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 10,
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: isReacting ? 0.5 : 1,
                  }}
                >
                  <Text style={{ fontSize: 20 }}>{emoji}</Text>
                </TouchableOpacity>
              )
            })}
          </View>
        </View>
      </View>
    </Modal>
  )
}
